import React, { createContext, useContext, useEffect, useReducer } from "react"

import { useCreateTeeupStore } from "stores/createTeeupStore"

import { defaultCoverFor } from "@utils/localCoverImage"

import { CreateTeeUpReducer } from "./CreateTeeUpReducer"

const CreateTeeUpStateContext = createContext({})
const CreateTeeUpDispatchContext = createContext(undefined)

// CREATE-COVER 2026-07-04: the cover area must never be empty from the moment the create form
// opens. Seed a deterministic default (same bundled TEEUP_IMAGES set + hash the card/detail
// surfaces already fall back to via defaultCoverFor) BEFORE any name exists. coverManuallySet /
// coverAutoAdjusted track whether the user has picked one themselves, or the one-time
// topic-aligned auto-adjust has already fired, so neither path ever double-fires or clobbers a
// real user choice (see CreateTeeUpReducer's SET_TEEUP_IMAGE / APPLY_AUTO_COVER handling).
const SEED_COVER = defaultCoverFor("")

const DEFAULT_STATE = {
  image: SEED_COVER,
  name: "",
  message: "",
  invitees: [],
  isInviteesOpen: false,
  coverManuallySet: false,
  coverAutoAdjusted: false,
}

// WN2 (Wave 4): lazy-init from a one-shot "Remake TeeUp" draft (name/image/invitees) if the
// kebab-menu Remake action set one on the global createTeeupStore just before opening this
// modal. A normal "+"-button create leaves remakeDraft null, so this is a pure pass-through
// (zero behavior change) for every non-remake create.
const buildInitialState = () => {
  const remakeDraft = useCreateTeeupStore.getState().remakeDraft
  if (!remakeDraft) return DEFAULT_STATE

  return {
    ...DEFAULT_STATE,
    name: remakeDraft.name || "",
    // A remade TeeUp's carried-over image is a real, previously-chosen cover -- treat it like a
    // manual pick (never auto-adjusted away). No image on the draft still gets the seed default.
    image: remakeDraft.image || SEED_COVER,
    coverManuallySet: !!remakeDraft.image,
    invitees: remakeDraft.invitees || [],
  }
}

const CreateTeeUpProvider = ({ children }) => {
  const [state, dispatch] = useReducer(CreateTeeUpReducer, null, buildInitialState)

  // Consume the draft exactly once so a SECOND, unrelated create (opened later in the same
  // session without going through Remake again) never accidentally reuses stale prefill data.
  useEffect(() => {
    if (useCreateTeeupStore.getState().remakeDraft) {
      useCreateTeeupStore.getState().setRemakeDraft(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <CreateTeeUpStateContext.Provider value={state}>
      <CreateTeeUpDispatchContext.Provider value={dispatch}>
        {children}
      </CreateTeeUpDispatchContext.Provider>
    </CreateTeeUpStateContext.Provider>
  )
}

const useCreateTeeUpState = () => {
  try {
    const context = useContext(CreateTeeUpStateContext)

    if (context === undefined) {
      throw new Error(
        "useCreateTeeUpState must be used within a CreateTeeUpProvider"
      )
    }

    return context
  } catch (error) {
    console.log(error)
  }
}

const useCreateTeeUpDispatch = () => {
  try {
    const context = useContext(CreateTeeUpDispatchContext)

    if (context === undefined) {
      throw new Error(
        "useCreateTeeUpDispatch must be used within a CreateTeeUpProvider"
      )
    }

    return context
  } catch (error) {
    console.log(error)
  }
}

export { CreateTeeUpProvider, useCreateTeeUpState, useCreateTeeUpDispatch }
